import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import PatientInformationForm from './components/PatientInformationForm';
import DrugInformationForm from './components/DrugInformationForm';
import AdverseEventForm from './components/AdverseEventForm';
import BlockchainVerificationPanel from './components/BlockchainVerificationPanel';
import RelatedEventsPanel from './components/RelatedEventsPanel';
import RegulatorySubmissionPanel from './components/RegulatorySubmissionPanel';

const AdverseEventReporting = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    patient: {},
    drug: {},
    event: {}
  });
  const [errors, setErrors] = useState({});
  const [verificationData, setVerificationData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportId, setReportId] = useState('');

  useEffect(() => {
    // Generate unique report ID
    const generateReportId = () => {
      const date = new Date();
      const year = date?.getFullYear();
      const month = String(date?.getMonth() + 1)?.padStart(2, '0');
      const day = String(date?.getDate())?.padStart(2, '0');
      const random = Math.floor(Math.random() * 9999)?.toString()?.padStart(4, '0');
      return `AE-${year}-${month}${day}-${random}`;
    };

    setReportId(generateReportId());
  }, []);

  const steps = [
    { id: 1, title: 'Patient Information', icon: 'User', description: 'Anonymized patient demographics' },
    { id: 2, title: 'Drug Information', icon: 'Pill', description: 'Suspect drug details' },
    { id: 3, title: 'Adverse Event', icon: 'AlertTriangle', description: 'Event description and severity' },
    { id: 4, title: 'Review & Submit', icon: 'CheckCircle', description: 'Final review and blockchain verification' }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.patient?.patientId) newErrors.patientId = 'Patient ID is required';
        if (!formData?.patient?.ageGroup) newErrors.ageGroup = 'Age group is required';
        break;
      case 2:
        if (!formData?.drug?.drugName) newErrors.drugName = 'Drug name is required';
        if (!formData?.drug?.activeIngredient) newErrors.activeIngredient = 'Active ingredient is required';
        if (!formData?.drug?.strength) newErrors.strength = 'Strength/dose is required';
        if (!formData?.drug?.route) newErrors.route = 'Route of administration is required';
        if (!formData?.drug?.startDate) newErrors.startDate = 'Treatment start date is required';
        break;
      case 3:
        if (!formData?.event?.onsetDate) newErrors.onsetDate = 'Event onset date is required';
        if (!formData?.event?.description) newErrors.description = 'Event description is required';
        if (!formData?.event?.severity) newErrors.severity = 'Severity assessment is required';
        if (!formData?.event?.outcome) newErrors.outcome = 'Event outcome is required';
        if (!formData?.event?.causality) newErrors.causality = 'Causality assessment is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handlePatientDataChange = (data) => {
    setFormData(prev => ({ ...prev, patient: data }));
    // Clear related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors)?.forEach(key => {
        if (key?.startsWith('patient') || ['patientId', 'ageGroup', 'gender', 'weight', 'height']?.includes(key)) {
          delete newErrors?.[key];
        }
      });
      return newErrors;
    });
  };

  const handleDrugDataChange = (data) => {
    setFormData(prev => ({ ...prev, drug: data }));
    // Clear related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors)?.forEach(key => {
        if (key?.startsWith('drug') || ['drugName', 'activeIngredient', 'strength', 'route', 'startDate']?.includes(key)) {
          delete newErrors?.[key];
        }
      });
      return newErrors;
    });
  };

  const handleEventDataChange = (data) => {
    setFormData(prev => ({ ...prev, event: data }));
    // Clear related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors)?.forEach(key => {
        if (key?.startsWith('event') || ['onsetDate', 'description', 'severity', 'outcome', 'causality']?.includes(key)) {
          delete newErrors?.[key];
        }
      });
      return newErrors;
    });
  };

  const handleVerificationComplete = (data) => {
    setVerificationData(data);
  };

  const handleFinalSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    
    try {
      // Simulate final submission process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Show success message and redirect
      alert('Adverse event report submitted successfully!');
      navigate('/quality-dashboard');
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/quality-dashboard')}
                  className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Adverse Event Reporting</h1>
                  <p className="text-muted-foreground">
                    Report ID: <span className="font-mono text-primary">{reportId}</span>
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">Dr. Maria Konstantinou</p>
                  <p className="text-xs text-muted-foreground">Pharmacovigilance Officer</p>
                </div>
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Shield" size={20} className="text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-muted/30 border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => (
                <div key={step?.id} className="flex items-center">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-150 ${
                      getStepStatus(step?.id) === 'completed' 
                        ? 'bg-success text-success-foreground' 
                        : getStepStatus(step?.id) === 'current' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground'
                    }`}>
                      <Icon 
                        name={getStepStatus(step?.id) === 'completed' ? 'Check' : step?.icon} 
                        size={16} 
                      />
                    </div>
                    <div className="hidden md:block">
                      <p className={`text-sm font-medium ${
                        getStepStatus(step?.id) === 'current' ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {step?.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{step?.description}</p>
                    </div>
                  </div>
                  
                  {index < steps?.length - 1 && (
                    <div className={`hidden md:block w-16 h-px mx-4 ${
                      getStepStatus(step?.id) === 'completed' ? 'bg-success' : 'bg-border'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form Area */}
            <div className="lg:col-span-2 space-y-8">
              {currentStep === 1 && (
                <PatientInformationForm
                  patientData={formData?.patient}
                  onPatientDataChange={handlePatientDataChange}
                  errors={errors}
                />
              )}

              {currentStep === 2 && (
                <DrugInformationForm
                  drugData={formData?.drug}
                  onDrugDataChange={handleDrugDataChange}
                  errors={errors}
                />
              )}

              {currentStep === 3 && (
                <AdverseEventForm
                  eventData={formData?.event}
                  onEventDataChange={handleEventDataChange}
                  errors={errors}
                />
              )}

              {currentStep === 4 && (
                <div className="space-y-6">
                  <BlockchainVerificationPanel
                    reportData={{ ...formData?.patient, ...formData?.drug, ...formData?.event }}
                    onVerificationComplete={handleVerificationComplete}
                  />
                  
                  <RegulatorySubmissionPanel
                    reportData={formData?.event}
                    verificationData={verificationData}
                  />
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>

                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/quality-dashboard')}
                  >
                    Save Draft
                  </Button>
                  
                  {currentStep < steps?.length ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={handleFinalSubmit}
                      loading={isSubmitting}
                      iconName="Send"
                      iconPosition="left"
                      disabled={!verificationData}
                    >
                      Submit Report
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Related Events Panel */}
              <RelatedEventsPanel currentDrug={formData?.drug?.drugName} />

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Search"
                    iconPosition="left"
                    onClick={() => navigate('/audit-trail-viewer')}
                  >
                    View Audit Trail
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Database"
                    iconPosition="left"
                    onClick={() => navigate('/batch-testing-records')}
                  >
                    Check Batch Records
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="FileText"
                    iconPosition="left"
                    onClick={() => navigate('/regulatory-compliance')}
                  >
                    Compliance Status
                  </Button>
                </div>
              </div>

              {/* Help & Support */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="HelpCircle" size={20} className="text-primary" />
                  <h3 className="font-semibold text-foreground">Need Help?</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Contact our pharmacovigilance team for assistance with adverse event reporting.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={14} className="text-primary" />
                    <span className="text-foreground">+30 210 123 4567</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Mail" size={14} className="text-primary" />
                    <span className="text-foreground">pv@ifet.gr</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdverseEventReporting;